import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useLocation, Link } from "react-router-dom";
import {
    ArrowLeft,
    Plus,
    UserRound,
    UserPlus,
    ChevronDown,
    Pencil,
    Printer,
    Users,
    ImagePlus,
    AlertTriangle,
    FilePlus,
} from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useApiBase, useRouteBase } from "../context/ApiBaseContext";
import { useRevalidateOnFocus } from "../lib/useRevalidateOnFocus";
import ProjectHeader from "../components/ProjectHeader";
import SceneEditDialog from "../components/SceneEditDialog";
import ContinuityBlock from "../components/ContinuityBlock";
import ConfirmDialog from "../components/ConfirmDialog";
import FlagButton from "../components/FlagButton";
import { characterAvatar } from "../lib/characterAvatar";
import { compareCharacters } from "../lib/characters";
import SupervisorNotesSection from "../components/SupervisorNotesSection";
import CharacterSceneAIBreakdown from "../components/CharacterSceneAIBreakdown";
import UnassignedAIBreakdownList from "../components/UnassignedAIBreakdownList";
import LookFilter from "../components/LookFilter";
import { useProjectAllergyMap } from "../lib/allergy";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import usePolling from "../hooks/usePolling";
import { printSceneContinuitySheet } from "../lib/printSheet";

/**
 * Lightweight attach panel — kept available so owners can still pick or
 * create a character to attach to this scene without leaving the page.
 * Collapsed by default so it doesn't compete with the continuity grid.
 *
 * iter-52 #12: now controllable from outside via `forceOpen` so the
 * dedicated "Manage cast" button on SceneDetail can scroll-and-open
 * the panel in one tap. The panel also gains an inline detach button
 * so adding/removing characters is a single round-trip per change.
 */
function AttachPanel({
    projectId,
    sceneId,
    attachedIds,
    attachedCharacters = [],
    onAttached,
    onDetached,
    canCreate,
    forceOpen,
    panelRef,
}) {
    const apiBase = useApiBase();
    const routeBase = useRouteBase();
    const [open, setOpen] = useState(false);
    const [projectChars, setProjectChars] = useState([]);
    const [loading, setLoading] = useState(false);
    const [newName, setNewName] = useState("");
    const [newActor, setNewActor] = useState("");
    // Iter-158.13.2.20f.20 · B1 — canonical Main/Crowd
    // classification exposed at the Create-New-&-Attach point.
    // Defaults to "main" so the surface preserves the pre-existing
    // implicit-create behaviour; Carla now explicitly picks Crowd
    // when creating background/crowd characters at the point of
    // need (e.g. Scene 17 pool crowd).
    const [newTeam, setNewTeam] = useState("main");
    const [creating, setCreating] = useState(false);
    const [detachingId, setDetachingId] = useState(null);
    const [error, setError] = useState("");
    const [filter, setFilter] = useState("");
    // Iter-157E P1(9) — Character removal confirmation. We never
    // drop a per-scene character association without an explicit
    // confirmation step. The copy explicitly reassures the user
    // that this is a scene-association only — continuity notes,
    // images and supervisor notes survive (the scene_characters
    // unlink only kills the per-pair character_scene_states row,
    // which is what a "remove from scene" semantically means).
    const [pendingDetach, setPendingDetach] = useState(null);

    // Sync with parent's "Manage cast" trigger.
    useEffect(() => {
        if (forceOpen) setOpen(true);
    }, [forceOpen]);

    useEffect(() => {
        if (!open) return;
        setLoading(true);
        api
            .get(`${apiBase}/${projectId}/characters`)
            .then(({ data }) => setProjectChars(data))
            .catch((e) => setError(formatApiError(e)))
            .finally(() => setLoading(false));
    }, [open, projectId]);

    const needle = filter.trim().toLowerCase();
    const unattached = projectChars
        .filter((c) => !attachedIds.has(c.id))
        .filter((c) => {
            if (!needle) return true;
            const hay = [
                c.name,
                c.actor,
                c.character_number ? `#${c.character_number}` : "",
            ]
                .filter(Boolean)
                .join(" ")
                .toLowerCase();
            return hay.includes(needle);
        });

    const attachExisting = async (characterId) => {
        setError("");
        try {
            const { data } = await api.post(
                `${apiBase}/${projectId}/scenes/${sceneId}/characters`,
                { character_id: characterId },
            );
            onAttached(data);
        } catch (e) {
            setError(formatApiError(e));
        }
    };

    const detach = async (characterId) => {
        setError("");
        setDetachingId(characterId);
        try {
            await api.delete(
                `${apiBase}/${projectId}/scenes/${sceneId}/characters/${characterId}`,
            );
            onDetached?.(characterId);
        } catch (e) {
            setError(formatApiError(e));
        } finally {
            setDetachingId(null);
            setPendingDetach(null);
        }
    };

    // Iter-157E P1(9) — open the confirmation gate instead of
    // detaching immediately. The actual call still routes through
    // `detach()` once the user confirms.
    const requestDetach = (character) => {
        setPendingDetach(character);
    };

    const createAndAttach = async (e) => {
        e.preventDefault();
        if (!newName.trim()) return;
        setCreating(true);
        setError("");
        try {
            const { data } = await api.post(
                `${apiBase}/${projectId}/scenes/${sceneId}/characters`,
                {
                    name: newName.trim(),
                    actor: newActor.trim() || null,
                    // Iter-158.13.2.20f.20 · B1 — persist the
                    // canonical Main/Crowd choice through to the
                    // implicit-create branch on both `/projects`
                    // and `/beta/hm-app/projects` backends.
                    team: newTeam === "crowd" ? "crowd" : "main",
                },
            );
            onAttached(data);
            setProjectChars((prev) => {
                if (prev.some((c) => c.id === data.id)) return prev;
                return [...prev, data];
            });
            setNewName("");
            setNewActor("");
            setNewTeam("main");
        } catch (e) {
            setError(formatApiError(e));
        } finally {
            setCreating(false);
        }
    };

    return (
        <section
            ref={panelRef}
            data-testid="attach-panel"
            className="rounded-xl border border-white/10 bg-card overflow-hidden scroll-mt-24"
        >
            <button
                data-testid="attach-panel-toggle"
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="tap w-full flex items-center justify-between gap-3 px-4 py-3 hover:bg-white/5"
            >
                <div className="flex items-center gap-3">
                    <div className="h-9 w-9 rounded-lg bg-primary/15 text-primary flex items-center justify-center">
                        <UserPlus size={16} />
                    </div>
                    <div className="text-left">
                        <div className="text-sm font-semibold text-white">
                            Attach characters to scene
                        </div>
                        <div className="text-xs text-neutral-500">
                            Pick existing or create new
                        </div>
                    </div>
                </div>
                <ChevronDown
                    size={16}
                    className={`text-neutral-500 transition-transform ${
                        open ? "rotate-180" : ""
                    }`}
                />
            </button>
            {open ? (
                <div className="border-t border-white/10 p-4 space-y-5">
                    {error ? (
                        <p data-testid="attach-error" className="text-sm text-red-400">
                            {error}
                        </p>
                    ) : null}

                    {attachedCharacters.length > 0 ? (
                        <div data-testid="attach-attached-section">
                            <Label className="text-xs uppercase tracking-[0.2em] text-neutral-500">
                                In this scene ({attachedCharacters.length})
                            </Label>
                            <ul className="mt-3 flex flex-wrap gap-2">
                                {attachedCharacters.map((c) => (
                                    <li key={c.id}>
                                        <button
                                            data-testid={`attach-detach-${c.id}`}
                                            type="button"
                                            disabled={detachingId === c.id}
                                            onClick={() => requestDetach(c)}
                                            title={`Remove ${c.name} from this scene`}
                                            className="tap inline-flex items-center gap-2 rounded-full bg-primary/10 border border-primary/30 px-3 py-1.5 text-sm text-white hover:bg-red-500/15 hover:border-red-400/40"
                                        >
                                            {c.character_number ? (
                                                <span className="mono text-xs text-white font-bold">
                                                    {c.character_number}
                                                </span>
                                            ) : null}
                                            <span className="truncate max-w-[140px]">
                                                {c.name}
                                            </span>
                                            <span className="text-[10px] uppercase tracking-[0.15em] text-neutral-300">
                                                {detachingId === c.id ? "…" : "Remove"}
                                            </span>
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ) : null}

                    {canCreate ? (
                        <form
                            onSubmit={createAndAttach}
                            className="space-y-3"
                            data-testid="attach-create-form"
                        >
                            <Label className="text-xs uppercase tracking-[0.2em] text-neutral-500">
                                Create new &amp; attach
                            </Label>
                            <Input
                                data-testid="attach-new-name"
                                placeholder="Character name"
                                value={newName}
                                onChange={(e) => setNewName(e.target.value)}
                                className="h-12 bg-secondary border-transparent focus-visible:ring-1 focus-visible:ring-primary"
                            />
                            <Input
                                data-testid="attach-new-actor"
                                placeholder="Actor (optional)"
                                value={newActor}
                                onChange={(e) => setNewActor(e.target.value)}
                                className="h-12 bg-secondary border-transparent focus-visible:ring-1 focus-visible:ring-primary"
                            />
                            {/* Iter-158.13.2.20f.20 · B1 — canonical
                                Main/Crowd segmented control. Matches
                                the visual grammar of CastHub,
                                OnSetMode, CreateCharacterDialog and
                                CharacterDetail. Defaults to Main so
                                Carla only has to touch it when
                                creating Crowd characters. */}
                            <div
                                role="radiogroup"
                                aria-label="Character team"
                                className="flex items-center gap-2"
                            >
                                <button
                                    type="button"
                                    role="radio"
                                    aria-checked={newTeam === "main"}
                                    data-testid="attach-new-team-main"
                                    onClick={() => setNewTeam("main")}
                                    className={`tap min-tap flex-1 h-10 rounded-md border text-xs font-semibold uppercase tracking-[0.15em] ${
                                        newTeam === "main"
                                            ? "bg-primary/15 border-primary/40 text-white"
                                            : "bg-secondary border-white/10 text-neutral-400 hover:text-white"
                                    }`}
                                >
                                    Main
                                </button>
                                <button
                                    type="button"
                                    role="radio"
                                    aria-checked={newTeam === "crowd"}
                                    data-testid="attach-new-team-crowd"
                                    onClick={() => setNewTeam("crowd")}
                                    className={`tap min-tap flex-1 h-10 rounded-md border text-xs font-semibold uppercase tracking-[0.15em] ${
                                        newTeam === "crowd"
                                            ? "bg-primary/15 border-primary/40 text-white"
                                            : "bg-secondary border-white/10 text-neutral-400 hover:text-white"
                                    }`}
                                >
                                    Crowd
                                </button>
                            </div>
                            <Button
                                data-testid="attach-new-submit"
                                type="submit"
                                disabled={creating || !newName.trim()}
                                className="w-full min-tap"
                                variant="secondary"
                            >
                                <Plus size={16} className="mr-2" />
                                {creating ? "Attaching…" : "Create & attach"}
                            </Button>
                        </form>
                    ) : null}
                    <div>
                        <Label className="text-xs uppercase tracking-[0.2em] text-neutral-500">
                            Add existing
                        </Label>
                        <Input
                            data-testid="attach-filter"
                            value={filter}
                            onChange={(e) => setFilter(e.target.value)}
                            placeholder="Search cast (name or number)"
                            className="mt-2 h-11 bg-secondary border-transparent focus-visible:ring-1 focus-visible:ring-primary"
                        />
                        {loading ? (
                            <p
                                data-testid="attach-loading"
                                className="mt-3 text-xs text-neutral-500"
                            >
                                Loading…
                            </p>
                        ) : unattached.length === 0 ? (
                            <p
                                data-testid="attach-no-available"
                                className="mt-3 text-xs text-neutral-500"
                            >
                                {needle
                                    ? `No matches for "${filter}".`
                                    : "Every project character is already attached."}
                            </p>
                        ) : (
                            <ul
                                data-testid="attach-available-list"
                                className="mt-3 flex flex-wrap gap-2"
                            >
                                {unattached.map((c) => (
                                    <li key={c.id}>
                                        <button
                                            data-testid={`attach-existing-${c.id}`}
                                            type="button"
                                            onClick={() => attachExisting(c.id)}
                                            className="tap min-tap inline-flex items-center gap-2 rounded-full bg-secondary border border-white/10 px-4 py-2 text-sm text-white hover:bg-white/10"
                                        >
                                            <Plus size={12} />
                                            {c.character_number ? (
                                                <span className="mono text-xs text-white font-bold">
                                                    {c.character_number}
                                                </span>
                                            ) : null}
                                            {c.name}
                                            {c.actor ? (
                                                <span className="text-neutral-500 text-xs ml-1">
                                                    {c.actor}
                                                </span>
                                            ) : null}
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>
            ) : null}

            {/* Iter-157E P1(9) — Confirmation gate for removing a
                character from a scene. The copy explicitly
                reassures the user that this is a scene-association
                only — continuity images, supervisor notes, and the
                character record itself are untouched. Only the
                per-pair character_scene_states row (continuity
                notes specific to THIS scene × character pair) is
                dropped, which matches the user's mental model of
                "I removed them from this scene". */}
            <ConfirmDialog
                open={Boolean(pendingDetach)}
                onOpenChange={(o) => {
                    if (!o) setPendingDetach(null);
                }}
                title={
                    pendingDetach
                        ? `Remove ${pendingDetach.name} from this scene?`
                        : "Remove from scene?"
                }
                description={
                    "This only removes the scene association. No continuity information will be deleted — images, supervisor notes and the character record itself stay intact. You can re-attach this character to the scene at any time."
                }
                confirmLabel="Remove"
                cancelLabel="Cancel"
                tone="danger"
                busy={detachingId === pendingDetach?.id}
                onConfirm={() => {
                    if (pendingDetach) detach(pendingDetach.id);
                }}
                testidPrefix="detach-character"
            />
        </section>
    );
}

export default function SceneDetail() {
    const { projectId, sceneId } = useParams();
    const apiBase = useApiBase();
    const routeBase = useRouteBase();
    const location = useLocation();
    const [project, setProject] = useState(null);
    const [scene, setScene] = useState(null);
    const [blocks, setBlocks] = useState([]); // continuity blocks (one per character)
    // iter-75 PART 4 — per-page Look filter state, scoped to this scene.
    const [lookFilter, setLookFilter] = useState("");
    const [error, setError] = useState("");
    const [editOpen, setEditOpen] = useState(false);
    // Iter-121 — intent-aware autofocus. When users enter Edit Scene
    // via the "+ Add scene look" affordance we focus the Look field
    // directly so they can't accidentally type into Episode. Reset
    // on every dialog close.
    const [editAutoFocus, setEditAutoFocus] = useState(null);
    const openEdit = (focusField = null) => {
        setEditAutoFocus(focusField);
        setEditOpen(true);
    };
    const closeEdit = () => {
        setEditOpen(false);
        setEditAutoFocus(null);
    };
    const [perms, setPerms] = useState(null);

    // Iter-158.13.2.20f.33 — H&M allergy indicator. Fetches all
    // project supervisor notes once (2000 cap, image_data
    // stripped) and returns a `Map<characterId, allergyNote>` of
    // Character-level notes tagged "allergy". Passed into each
    // per-character `ContinuityBlock` as an `allergyNote` prop so
    // the safety indicator appears exactly where the Character is
    // being worked on. Refreshes on
    // `onsetflow:allergy-note-changed` (fired by CharacterDetail)
    // without a full page reload.
    const { byCharacterId: allergyByCharacterId } = useProjectAllergyMap(
        apiBase,
        projectId,
    );

    const isOwner = project?.role === "owner";
    const isAdmin = project?.role === "admin";
    const isContributor = project?.role === "contributor";
    // Iter-156A.5 Phase 5 — archive read-only enforcement on the FE.
    const isArchived = Boolean(project?.archived);
    // Iter-97 / Iter-156D — Add Note + Edit/Delete are now scoped
    // to the write-capable role set: Owner, Administrator,
    // Contributor. Viewers stay read-only. Per-row Edit/Delete is
    // additionally creator-or-admin scoped inside
    // SupervisorNotesList (backend enforces the same).
    const canManageNotes =
        (isOwner || isAdmin || isContributor) && !isArchived;
    // Iter-156D — admin-override for note row-actions + document
    // governance items inside SupervisorNotesSection. Contributors
    // can ADD notes (canManageNotes) but only Owner/Admin can edit
    // / delete OTHER PEOPLE'S notes or use the Paste/Upload tools.
    const canAdminNotes = (isOwner || isAdmin) && !isArchived;
    // Iter-83 / Iter-107 — Cast/Scenes caps. `canCreate` powers the
    // AttachPanel "create new character" affordance; `canEditScene`
    // powers the pencil + Manage cast buttons. Both fall back to the
    // legacy `can_create_characters` flag for back-compat. Admins
    // always pass via the all-true permissions surfaced by /permissions/me.
    const canCreate =
        !isArchived &&
        (isOwner ||
            isAdmin ||
            Boolean(perms?.permissions?.cast_add) ||
            Boolean(perms?.permissions?.can_create_characters));
    const canEditScene =
        !isArchived &&
        (isOwner || isAdmin || Boolean(perms?.permissions?.scenes_edit));
    const backHref = `${routeBase}/${projectId}/scenes/${sceneId}`;
    const backLabel = scene?.scene_number
        ? `Sc ${scene.scene_number}`
        : "Back to scene";

    // iter-52 #12: dedicated "Manage cast" button → scrolls to + opens
    // the AttachPanel in one tap. The pencil edit dialog stays focused
    // on heading/INT-EXT/TOD/episode and is not crowded by character
    // assignment.
    const attachPanelRef = useRef(null);
    const [attachForceOpen, setAttachForceOpen] = useState(0);
    const openManageCast = () => {
        setAttachForceOpen((n) => n + 1);
        // Scroll happens after the panel opens & renders its content.
        setTimeout(() => {
            attachPanelRef.current?.scrollIntoView({
                behavior: "smooth",
                block: "start",
            });
        }, 80);
    };

    const loadContinuity = useCallback(async () => {
        try {
            const { data } = await api.get(
                `${apiBase}/${projectId}/scenes/${sceneId}/continuity`,
            );
            setScene(data.scene);
            // Iter-158.13.2.20f.19d — reuse the canonical iter-129
            // `compareCharacters` helper so the Characters & Continuity
            // section renders in ascending natural-numeric order
            // (1, 2, 3, 4, 6, 11, 36 — not the string sort 1, 11, 2, 3,
            // 36, 4, 6 Carla observed on real iPhone Safari). Continuity
            // blocks wrap character objects, so we compare `.character`
            // rather than the block itself. Legacy/non-integer numbers
            // and numberless characters fall through to the helper's
            // deterministic tail-sort (alphabetical by name at the end).
            const sorted = Array.isArray(data.characters)
                ? [...data.characters].sort((a, b) =>
                      compareCharacters(a?.character, b?.character),
                  )
                : [];
            setBlocks(sorted);
        } catch (e) {
            setError(formatApiError(e));
        }
    }, [projectId, sceneId]);

    const loadProjectMeta = useCallback(async () => {
        try {
            const [{ data: p }, { data: pm }] = await Promise.all([
                api.get(`${apiBase}/${projectId}`),
                api.get(`${apiBase}/${projectId}/permissions/me`).catch(() => ({
                    data: null,
                })),
            ]);
            setProject(p);
            setPerms(pm);
        } catch (e) {
            setError(formatApiError(e));
        }
    }, [projectId]);

    useEffect(() => {
        loadProjectMeta();
    }, [loadProjectMeta]);

    // Iter-156E — refetch project + caps on tab focus so out-of-band
    // governance changes (role flips, archive, script visibility,
    // notes visibility) propagate naturally without manual refresh.
    useRevalidateOnFocus(loadProjectMeta);

    useEffect(() => {
        loadContinuity();
    }, [loadContinuity]);

    // After AssignmentDialog saves a new entry tied to this scene we
    // refresh the continuity to surface the new image immediately.
    useEffect(() => {
        const onSaved = () => loadContinuity();
        window.addEventListener("onsetflow:state-entry-saved", onSaved);
        return () =>
            window.removeEventListener("onsetflow:state-entry-saved", onSaved);
    }, [loadContinuity]);

    // Iter-95 — background polling so new entries from other devices
    // (or other team members) appear without a manual refresh.
    usePolling(loadContinuity, { intervalMs: 12000 });

    const onAttached = async (character) => {
        // Optimistically pre-pend an empty block; the next continuity
        // refetch (after AssignmentDialog or manual reload) will fill in
        // any subsequent state_entries.
        //
        // Iter-158.13.2.20f.19d — resort with `compareCharacters` after
        // insertion so the newly-attached character lands in its correct
        // natural-numeric slot instead of appearing at the tail.
        setBlocks((prev) => {
            if (prev.some((b) => b.character.id === character.id)) return prev;
            const next = [
                ...prev,
                {
                    character,
                    notes: "",
                    notes_updated_at: null,
                    notes_updated_by_name: null,
                    entries: [],
                    entry_count: 0,
                },
            ];
            return next.sort((a, b) =>
                compareCharacters(a?.character, b?.character),
            );
        });
    };

    const onDetached = (characterId) => {
        // Optimistically drop the block; loadContinuity() is also fine
        // but instant feedback feels right when the trash button is on
        // the same surface.
        setBlocks((prev) => prev.filter((b) => b.character.id !== characterId));
    };

    const handleNotesChange = (characterId, next) => {
        setBlocks((prev) =>
            prev.map((b) =>
                b.character.id === characterId
                    ? {
                          ...b,
                          notes: next.notes,
                          notes_updated_at: next.notes_updated_at,
                          notes_updated_by_name: next.notes_updated_by_name,
                      }
                    : b,
            ),
        );
    };

    const triggerCapture = (characterId) => (_action) => {
        // Iter-122 — camera capture disabled globally. Whatever action
        // the ContinuityBlock buttons report (legacy "camera" or new
        // "photos"), we dispatch "photos" to ensure the upload picker
        // opens. Context (scene, character, episode, mode) is still
        // forwarded so AssignmentDialog pre-fills correctly.
        window.dispatchEvent(
            new CustomEvent("onsetflow:capture", {
                detail: {
                    action: "photos",
                    context: {
                        mode: "onset",
                        scene_id: sceneId,
                        character_id: characterId,
                        episode: scene?.episode || undefined,
                    },
                },
            }),
        );
    };

    return (
        <div data-testid="scene-detail-page" className="min-h-screen">
            <ProjectHeader projectName={project?.name} subtitle="Scene detail" />

            <main className="max-w-2xl mx-auto px-5 pt-6 pb-48">
                {/* iter-69 PART 2 — Predictable Back navigation.
                    The list page that opened this scene (OnSetMode,
                    PrepMode, ScenesHub) passes `state.returnTo` with
                    its own URL + label. We honour that exactly so
                    "Back to scenes" always returns the user to where
                    they came from — never flips them between
                    Fittings ↔ On Set silently.
                    Fallback: when there's no nav state (deep link,
                    refresh, capture-fab "Open scene" follow-up), we
                    use the user's last-saved working lens from
                    localStorage as a best-guess return target. */}
                {/* Iter-155B.1 — episode-aware Back navigation.
                    When the user came from ScenesHub via a specific
                    episode, we restore that episode expanded + scroll
                    the previously-opened scene back into view, so the
                    user lands back exactly where they left off (rather
                    than at "Scenes Overview" with all episodes
                    collapsed again — the iter-102 regression). */}
                {(() => {
                    const navReturn = location.state?.returnTo;
                    let backTo;
                    let backLabel;
                    let backState;
                    if (navReturn?.path) {
                        backTo = navReturn.path;
                        backLabel = navReturn.label || "Back";
                        backState = {
                            expandEpisode: navReturn.expandEpisode || null,
                            scrollToSceneId: navReturn.scrollToSceneId || sceneId,
                        };
                    } else {
                        const lens = (() => {
                            try {
                                const v = window.localStorage.getItem("onsetflow:scenes-lens");
                                return v === "prep" ? "prep" : "onset";
                            } catch { return "onset"; }
                        })();
                        backTo = `${routeBase}/${projectId}/${lens}`;
                        backLabel = lens === "prep" ? "Back to Fittings" : "Back to scenes";
                        backState = { scrollToSceneId: sceneId };
                    }
                    return (
                        <Link
                            data-testid="scene-back-to-list"
                            to={backTo}
                            state={backState}
                            className="tap inline-flex items-center gap-2 text-sm text-neutral-400 hover:text-white mb-5"
                        >
                            <ArrowLeft size={14} /> {backLabel}
                        </Link>
                    );
                })()}

                {error ? (
                    <p data-testid="scene-detail-error" className="text-sm text-red-400">
                        {error}
                    </p>
                ) : !scene ? (
                    <div
                        data-testid="scene-detail-loading"
                        className="h-40 rounded-xl border border-white/10 bg-card animate-pulse"
                    />
                ) : (
                    <>
                        <section
                            data-testid="scene-detail-meta"
                            className={`rounded-xl border ${
                                scene.omitted
                                    ? "border-dashed border-amber-400/60 bg-amber-500/10"
                                    : "border-white/10 bg-card"
                            } p-4 sm:p-5 mb-5`}
                        >
                            {/* Iter-158.13.2.20f.33 — responsive Scene
                                header. Previously `flex items-start`
                                packed three columns (number badge,
                                heading + badges, edit actions) on the
                                same row from 320px up. Real-iPhone
                                (390px) rendered composite refs like
                                `1.01.17` + INT/EXT + TOD pills + edit
                                buttons in a row that squeezed the
                                heading area — Carla read it as the
                                Scene reference "crushing" the
                                heading. Switch to `flex-col` on
                                mobile so the number badge stacks
                                above and the heading gets the full
                                width; revert to `flex-row` on `sm:`
                                (≥640px) so tablets/desktop keep the
                                side-by-side identity layout. Number,
                                heading, and edit-buttons blocks
                                retain their existing testids and
                                inner markup verbatim. */}
                            <div className="flex flex-col sm:flex-row sm:items-start gap-3 sm:gap-4">
                                {/* Iter-158.13.2.20f.21 — mobile scene-number
                                    protection. The badge is now a
                                    `min-w-*` auto-grow container with
                                    `shrink-0` + `whitespace-nowrap`, so
                                    long composites like 1.01.17 / 1.01.23
                                    stay on one line at a slightly gentler
                                    font size and never spill into the
                                    INT/EXT · DAY/NIGHT pill row. The
                                    number-only case (film productions,
                                    no episode) keeps the larger display
                                    because there's no crowding risk. */}
                                <div
                                    data-testid="scene-detail-number"
                                    className="mono font-bold text-white text-center shrink-0 whitespace-nowrap tabular-nums px-1 min-w-12 sm:min-w-16"
                                >
                                    {scene.episode && scene.scene_number
                                        ? (
                                            <span className="text-lg sm:text-xl tracking-tight">
                                                {scene.episode}.{scene.scene_number}
                                            </span>
                                        )
                                        : (
                                            <span className="text-2xl sm:text-3xl">
                                                {scene.scene_number || "—"}
                                            </span>
                                        )}
                                </div>
                                <div className="min-w-0 flex-1">
                                    {/* Iter-158.13.2.20f.3 #5 — mobile scene-detail
                                        header decluttering. The `1.4` number badge
                                        to the left already carries the episode
                                        context, so the standalone "Ep 1" pill was
                                        redundant clutter on 390px screens. It now
                                        renders ONLY on projects where the composite
                                        badge cannot show the episode (edge case:
                                        episode present but scene_number missing).
                                        INT/EXT + TOD are compact tighter-tracked
                                        pills so they wrap predictably. */}
                                    <div className="flex items-center gap-1.5 flex-wrap">
                                        {scene.episode && !scene.scene_number ? (
                                            <span
                                                data-testid="scene-detail-episode"
                                                className="text-[10px] uppercase tracking-[0.2em] px-2 py-0.5 rounded-full bg-primary/15 text-primary border border-primary/30 font-semibold"
                                            >
                                                Ep {scene.episode}
                                            </span>
                                        ) : null}
                                        {scene.omitted ? (
                                            <span
                                                data-testid="scene-detail-omitted-badge"
                                                className="text-[10px] uppercase tracking-[0.25em] px-2 py-0.5 rounded-full bg-amber-500/30 text-amber-50 border border-amber-400/60 font-semibold"
                                                title={scene.omitted_by_revision_id ? "Marked omitted by an approved script revision. Continuity preserved." : "Marked omitted in the original script. Continuity preserved."}
                                            >
                                                {scene.omitted_by_revision_id ? "Omitted by revision" : "Omitted in script"}
                                            </span>
                                        ) : null}
                                        {scene.added_by_revision_id && !scene.omitted ? (
                                            <span
                                                data-testid="scene-detail-inserted-badge"
                                                className="text-[10px] uppercase tracking-[0.25em] px-2 py-0.5 rounded-full bg-emerald-500/25 text-emerald-100 border border-emerald-400/50 font-semibold"
                                                title="This scene was inserted by an approved script revision."
                                            >
                                                Inserted by revision
                                            </span>
                                        ) : null}
                                        {scene.int_ext ? (
                                            <span
                                                data-testid="scene-detail-intext"
                                                className="text-[10px] uppercase tracking-[0.25em] px-2 py-0.5 rounded-full bg-secondary text-neutral-200"
                                            >
                                                {scene.int_ext}
                                            </span>
                                        ) : null}
                                        {scene.time_of_day ? (
                                            <span
                                                data-testid="scene-detail-tod"
                                                className="text-[10px] uppercase tracking-[0.25em] px-2 py-0.5 rounded-full bg-secondary text-neutral-200"
                                            >
                                                {scene.time_of_day}
                                            </span>
                                        ) : null}
                                    </div>
                                    <div
                                        data-testid="scene-detail-heading"
                                        className="font-display text-lg sm:text-xl font-bold text-white mt-2 leading-tight break-words [overflow-wrap:anywhere]"
                                    >
                                        {scene.heading}
                                    </div>
                                    {scene.story_time ? (
                                        <div
                                            data-testid="scene-detail-story-time"
                                            className="mt-1.5 text-[11px] uppercase tracking-[0.2em] text-neutral-400"
                                        >
                                            {scene.story_time}
                                        </div>
                                    ) : null}
                                </div>
                                {canEditScene ? (
                                    <div className="shrink-0 flex items-center gap-1.5">
                                        {/* Iter-125 — scene-level Upload.
                                            One-tap affordance to add an
                                            image to THIS scene (character
                                            is picked in the AssignmentDialog
                                            that follows). Uses the same
                                            `onsetflow:capture` pipeline as
                                            the per-row Upload buttons so
                                            context (scene + episode + mode)
                                            is pre-filled. */}
                                        <button
                                            type="button"
                                            data-testid="scene-detail-upload-btn"
                                            data-action="upload"
                                            onClick={() => triggerCapture(null)("photos")}
                                            title="Upload image to this scene"
                                            aria-label="Upload image to this scene"
                                            className="tap shrink-0 inline-flex items-center justify-center rounded-md h-10 w-10 bg-primary text-primary-foreground hover:bg-primary/90"
                                        >
                                            <ImagePlus size={16} />
                                        </button>
                                        <FlagButton
                                            projectId={projectId}
                                            entityType="scene"
                                            entityId={scene.id}
                                            entityLabel={`Sc ${scene.scene_number || ""}`.trim()}
                                            testid="scene-detail-flag"
                                            size="lg"
                                        />
                                        <button
                                            data-testid="scene-detail-edit-btn"
                                            type="button"
                                            onClick={() => openEdit(null)}
                                            title="Edit scene"
                                            className="tap shrink-0 inline-flex items-center justify-center rounded-md h-10 w-10 text-neutral-300 hover:text-white hover:bg-white/5"
                                        >
                                            <Pencil size={16} />
                                        </button>
                                    </div>
                                ) : (
                                    <FlagButton
                                        projectId={projectId}
                                        entityType="scene"
                                        entityId={scene.id}
                                        entityLabel={`Sc ${scene.scene_number || ""}`.trim()}
                                        testid="scene-detail-flag"
                                        size="lg"
                                    />
                                )}
                            </div>
                            {/* Iter-158.13.2.20f.19c — Description + Look
                                blocks lifted OUT of the header flex row.
                                On iPhone 390px the middle column was
                                squeezed to ~154px between the number
                                badge and the action buttons, forcing
                                realistic descriptions into a very narrow
                                column. Placing them below the header
                                row gives them the full card width while
                                preserving the .20f.19b vertical stacking
                                (Description above Look) and every other
                                UAT-passed behaviour. */}
                            {scene.description ? (
                                <button
                                    type="button"
                                    data-testid="scene-detail-description"
                                    onClick={canEditScene ? () => openEdit("description") : undefined}
                                    disabled={!canEditScene}
                                    className={`mt-3 w-full text-left rounded-lg bg-secondary/40 border border-white/10 px-3 py-2.5 ${canEditScene ? "hover:bg-secondary/60 cursor-pointer" : "cursor-default"}`}
                                    title={canEditScene ? "Tap to edit scene description" : ""}
                                >
                                    <span className="block text-[10px] uppercase tracking-[0.2em] text-neutral-500 mb-1">
                                        Description
                                    </span>
                                    <span className="block text-sm text-neutral-300 leading-relaxed whitespace-pre-wrap break-words">
                                        {scene.description}
                                    </span>
                                </button>
                            ) : canEditScene ? (
                                <button
                                    type="button"
                                    data-testid="scene-detail-description-add"
                                    onClick={() => openEdit("description")}
                                    className="mt-3 flex w-fit items-center gap-1.5 text-[11px] uppercase tracking-[0.2em] text-neutral-400 hover:text-white"
                                >
                                    + Add scene description
                                </button>
                            ) : null}
                            {scene.look ? (
                                <button
                                    type="button"
                                    data-testid="scene-detail-look"
                                    onClick={canEditScene ? () => openEdit("look") : undefined}
                                    disabled={!canEditScene}
                                    className={`mt-3 inline-flex items-center gap-2 px-3 h-8 rounded-full bg-primary/10 border border-primary/30 text-primary text-xs font-semibold tracking-wide ${canEditScene ? "hover:bg-primary/20 cursor-pointer" : "cursor-default"}`}
                                    title={canEditScene ? "Tap to edit scene look" : ""}
                                >
                                    <span className="uppercase text-[10px] tracking-[0.2em] opacity-70">Look</span>
                                    <span className="text-white">{scene.look}</span>
                                </button>
                            ) : canEditScene ? (
                                <button
                                    type="button"
                                    data-testid="scene-detail-look-add"
                                    onClick={() => openEdit("look")}
                                    className="mt-3 flex w-fit items-center gap-1.5 text-[11px] uppercase tracking-[0.2em] text-neutral-400 hover:text-white"
                                >
                                    + Add scene look
                                </button>
                            ) : null}
                            {/* Iter-158.13.2.20f.42-char-workspace — Scene
                                notes surface. The field is already returned
                                by GET /projects/{pid}/scenes/{sid} and
                                editable via Bulk Edit Scenes; SceneDetail
                                previously accepted the field but never
                                rendered it. Presentation-only here — no
                                edit affordance is added in this build. */}
                            {scene.notes ? (
                                <div
                                    data-testid="scene-detail-notes"
                                    className="mt-3 w-full rounded-lg bg-secondary/40 border border-white/10 px-3 py-2.5"
                                >
                                    <span className="block text-[10px] uppercase tracking-[0.2em] text-neutral-500 mb-1">
                                        Scene notes
                                    </span>
                                    <span className="block text-sm text-neutral-300 leading-relaxed whitespace-pre-wrap break-words">
                                        {scene.notes}
                                    </span>
                                </div>
                            ) : null}
                            {/* iter-52 #3: action row moved BELOW the
                                metadata so the heading can breathe and
                                "Manage cast" stays full-width at every
                                viewport without crowding the title. */}
                            {/* Iter-118.13 PART 2 — at-a-glance Look
                                summary for this scene. Pulls every
                                distinct Look across all continuity
                                entries (regardless of character) so the
                                supervisor sees the looks-in-play without
                                scrolling. Hidden on scenes with 0 looks
                                so we don't add empty UI weight. */}
                            {(() => {
                                const distinctLooks = Array.from(
                                    new Set(
                                        (blocks || []).flatMap((b) =>
                                            (b.entries || [])
                                                .map((e) => (e?.look || "").trim())
                                                .filter(Boolean),
                                        ),
                                    ),
                                );
                                if (distinctLooks.length === 0) return null;
                                return (
                                    <div
                                        data-testid="scene-detail-looks-summary"
                                        className="mt-4 pt-3 border-t border-white/5"
                                    >
                                        <div className="text-[10px] uppercase tracking-[0.25em] text-neutral-500 mb-1.5">
                                            Looks in this scene · {distinctLooks.length}
                                        </div>
                                        <div className="flex flex-wrap gap-1.5">
                                            {distinctLooks.map((lk) => (
                                                <button
                                                    key={lk}
                                                    type="button"
                                                    data-testid={`scene-detail-look-chip-${lk}`}
                                                    onClick={() =>
                                                        setLookFilter(
                                                            (lookFilter || "").toLowerCase() === lk.toLowerCase()
                                                                ? ""
                                                                : lk,
                                                        )
                                                    }
                                                    title={`Filter to "${lk}" look`}
                                                    className={`instant-tap inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs border ${
                                                        (lookFilter || "").toLowerCase() === lk.toLowerCase()
                                                            ? "bg-primary text-primary-foreground border-primary font-semibold"
                                                            : "bg-secondary text-neutral-200 border-white/10 hover:bg-white/10"
                                                    }`}
                                                >
                                                    {lk}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                );
                            })()}
                            {canEditScene ? (
                                <div className="mt-4 pt-3 border-t border-white/5 flex items-center gap-2">
                                    <button
                                        data-testid="scene-manage-cast-btn"
                                        type="button"
                                        onClick={openManageCast}
                                        title="Manage cast for this scene"
                                        className="tap min-tap inline-flex items-center justify-center gap-1.5 rounded-md flex-1 h-10 px-3 text-xs font-semibold uppercase tracking-[0.15em] text-white bg-primary/15 border border-primary/30 hover:bg-primary/25"
                                    >
                                        <Users size={14} />
                                        Manage cast
                                    </button>
                                    <button
                                        data-testid="scene-print-continuity"
                                        type="button"
                                        onClick={() =>
                                            printSceneContinuitySheet({
                                                productionName: project?.name,
                                                scene,
                                                characters: blocks,
                                                projectId,
                                                apiBase,
                                            })
                                        }
                                        title="Print A4 continuity sheet for this scene"
                                        className="tap min-tap inline-flex items-center justify-center gap-1.5 rounded-md h-10 px-3 text-xs font-semibold uppercase tracking-[0.15em] text-neutral-200 bg-secondary border border-white/10 hover:bg-white/10"
                                    >
                                        <Printer size={14} />
                                        Print sheet
                                    </button>
                                </div>
                            ) : (
                                <div className="mt-4 pt-3 border-t border-white/5 flex items-center gap-2">
                                    <button
                                        data-testid="scene-print-continuity"
                                        type="button"
                                        onClick={() =>
                                            printSceneContinuitySheet({
                                                productionName: project?.name,
                                                scene,
                                                characters: blocks,
                                                projectId,
                                                apiBase,
                                            })
                                        }
                                        title="Print A4 continuity sheet for this scene"
                                        className="tap min-tap inline-flex items-center justify-center gap-1.5 rounded-md flex-1 h-10 px-3 text-xs font-semibold uppercase tracking-[0.15em] text-neutral-200 bg-secondary border border-white/10 hover:bg-white/10"
                                    >
                                        <Printer size={14} />
                                        Print continuity sheet
                                    </button>
                                </div>
                            )}
                        </section>

                        {scene.omitted && scene.omitted_by_revision_id ? (
                            <section
                                data-testid="scene-detail-omitted-banner"
                                className="mb-5 rounded-xl border border-amber-400/60 bg-amber-500/15 px-4 py-3 flex items-start gap-3"
                            >
                                <AlertTriangle size={16} className="text-amber-200 mt-0.5 shrink-0" />
                                <div className="text-[12px] text-amber-50 leading-relaxed">
                                    <span className="font-semibold uppercase tracking-[0.2em] text-amber-50 text-[11px]">
                                        Omitted by revision
                                    </span>
                                    <p className="mt-1 text-amber-50/95">
                                        This scene was marked omitted by an approved script revision. Continuity is preserved — photos, notes, looks and flags remain accessible below.
                                    </p>
                                </div>
                            </section>
                        ) : scene.omitted ? (
                            <section
                                data-testid="scene-detail-omitted-in-script-banner"
                                className="mb-5 rounded-xl border border-amber-400/60 bg-amber-500/15 px-4 py-3 flex items-start gap-3"
                            >
                                <AlertTriangle size={16} className="text-amber-200 mt-0.5 shrink-0" />
                                <div className="text-[12px] text-amber-50 leading-relaxed">
                                    <span className="font-semibold uppercase tracking-[0.2em] text-amber-50 text-[11px]">
                                        Omitted in script
                                    </span>
                                    <p className="mt-1 text-amber-50/95">
                                        This scene is marked omitted in the original shooting script. Continuity is preserved — photos, notes, looks and flags remain accessible below.
                                    </p>
                                </div>
                            </section>
                        ) : scene.added_by_revision_id ? (
                            <section
                                data-testid="scene-detail-inserted-banner"
                                className="mb-5 rounded-xl border border-emerald-400/30 bg-emerald-400/5 px-4 py-3 flex items-start gap-3"
                            >
                                <FilePlus size={16} className="text-emerald-300 mt-0.5 shrink-0" />
                                <div className="text-[12px] text-emerald-100 leading-relaxed">
                                    <span className="font-semibold uppercase tracking-[0.2em] text-emerald-200 text-[11px]">
                                        Inserted by revision
                                    </span>
                                    <p className="mt-1 text-emerald-100/90">
                                        This scene was created by an approved script revision. Add cast, notes and continuity below — it behaves like any other scene.
                                    </p>
                                </div>
                            </section>
                        ) : null}

                        <section className="mb-5">
                            <h2 className="text-xs uppercase tracking-[0.25em] text-neutral-500 flex items-center gap-2 mb-1">
                                <UserRound size={12} />
                                Characters &amp; continuity ({blocks.length})
                            </h2>
                            <p className="text-[11px] text-neutral-500 mb-3">
                                Characters in this scene
                            </p>

                            {blocks.length === 0 ? (
                                <div
                                    data-testid="scene-blocks-empty"
                                    className="rounded-xl border border-dashed border-white/10 p-8 text-center"
                                >
                                    <Users
                                        size={22}
                                        className="mx-auto text-neutral-500"
                                    />
                                    <p className="mt-3 text-sm text-neutral-300">
                                        No characters in this scene yet.
                                    </p>
                                    <p className="text-xs text-neutral-500 mt-1">
                                        Attach a character below to start tracking
                                        their look for this scene.
                                    </p>
                                </div>
                            ) : (
                                <>
                                    {/* iter-75 PART 4 — Look filter rail
                                        on the scene side too. Lets DOPs
                                        isolate a single Look across all
                                        cast in a scene at a glance. */}
                                    {(() => {
                                        const allLooks = [];
                                        for (const b of blocks) {
                                            for (const e of b.entries || []) {
                                                if (e?.look) allLooks.push(e.look);
                                            }
                                        }
                                        return (
                                            <LookFilter
                                                looks={allLooks}
                                                value={lookFilter}
                                                onChange={setLookFilter}
                                                testidPrefix="scene-look-filter"
                                                className="mb-3"
                                            />
                                        );
                                    })()}
                                    <ul
                                        data-testid="scene-blocks-list"
                                        className="space-y-4"
                                    >
                                        {blocks.map((b) => {
                                            const c = b.character;
                                            const filteredEntries = lookFilter
                                                ? (b.entries || []).filter(
                                                      (e) =>
                                                          (e.look || "").toLowerCase() ===
                                                          lookFilter.toLowerCase(),
                                                  )
                                                : b.entries;
                                            if (
                                                lookFilter &&
                                                (!filteredEntries ||
                                                    filteredEntries.length === 0)
                                            ) {
                                                return null;
                                            }
                                        // iter-67 PART 1: number-first
                                        // avatar so the production
                                        // identifier survives into the
                                        // scene-level continuity card.
                                        const initial = characterAvatar(c);
                                        return (
                                            <li key={c.id}>
                                                <ContinuityBlock
                                                    projectId={projectId}
                                                    sceneId={sceneId}
                                                    characterId={c.id}
                                                    scene={scene}
                                                    allergyNote={allergyByCharacterId.get(c.id) || null}
                                                    canEditNotes={
                                                        isOwner ||
                                                        isAdmin ||
                                                        Boolean(perms?.permissions?.cast_edit)
                                                    }
                                                    header={{
                                                        kind: "character",
                                                        avatar: initial,
                                                        primary: (
                                                            <span className="flex items-center gap-2">
                                                                <span className="truncate">
                                                                    {c.name}
                                                                </span>
                                                                {c.team === "crowd" ? (
                                                                    <span className="text-[9px] uppercase tracking-[0.15em] text-amber-200 bg-amber-500/10 border border-amber-500/30 rounded-full px-1.5">
                                                                        Crowd
                                                                    </span>
                                                                ) : null}
                                                            </span>
                                                        ),
                                                        secondary: c.actor ? (
                                                            <>
                                                                {c.actor} ·{" "}
                                                                {b.entry_count} image
                                                                {b.entry_count === 1
                                                                    ? ""
                                                                    : "s"}
                                                            </>
                                                        ) : (
                                                            <>
                                                                {b.entry_count} image
                                                                {b.entry_count === 1
                                                                    ? ""
                                                                    : "s"}
                                                            </>
                                                        ),
                                                        to: `${routeBase}/${projectId}/characters/${c.id}`,
                                                    }}
                                                    notes={b.notes}
                                                    notesUpdatedAt={
                                                        b.notes_updated_at
                                                    }
                                                    notesUpdatedByName={
                                                        b.notes_updated_by_name
                                                    }
                                                    entries={filteredEntries}
                                                    backHref={backHref}
                                                    backLabel={backLabel}
                                                    onCapture={triggerCapture(c.id)}
                                                    onNotesChange={(next) =>
                                                        handleNotesChange(c.id, next)
                                                    }
                                                />
                                                {/* Iter-158.13.2.20f.42-relational
                                                    — the AI Hair & Makeup
                                                    Breakdown records that
                                                    relate to BOTH this Scene
                                                    AND this Character live
                                                    directly under the
                                                    Character's continuity
                                                    surface. Same physical
                                                    `hm_breakdown_entries`
                                                    documents surfaced in
                                                    Bulk Edit Scenes and
                                                    Cast → Character → Scene
                                                    — one record, three
                                                    presentations. */}
                                                <CharacterSceneAIBreakdown
                                                    apiBase={apiBase}
                                                    projectId={projectId}
                                                    sceneId={sceneId}
                                                    characterId={c.id}
                                                    testidPrefix="scene-char-ai"
                                                />
                                            </li>
                                        );
                                    })}
                                    </ul>
                                </>
                            )}
                        </section>

                        <AttachPanel
                            projectId={projectId}
                            sceneId={sceneId}
                            attachedIds={
                                new Set(blocks.map((b) => b.character.id))
                            }
                            attachedCharacters={blocks.map((b) => b.character)}
                            onAttached={onAttached}
                            onDetached={onDetached}
                            canCreate={canCreate}
                            forceOpen={attachForceOpen}
                            panelRef={attachPanelRef}
                        />

                        <SupervisorNotesSection
                            projectId={projectId}
                            sceneId={sceneId}
                            isOwner={canManageNotes}
                            canAdmin={canAdminNotes}
                            showSceneRef={false}
                            testid="scene-supervisor-notes"
                            emptyHint="No notes for this scene yet."
                        />

                        {/* Iter-158.13.2.20f.42-relational — the
                            Scene-level H&M Working Breakdown mount
                            was replaced. Character-related findings
                            now live under each Character's own
                            AI Hair & Makeup Breakdown accordion
                            (above), so a scene-only DeptBreakdownSection
                            would have duplicated those rows here.
                            This section carries ONLY findings whose
                            character_ids array is empty — the truly
                            Scene-wide leftovers — filtered client-side
                            from the SAME hm_breakdown_entries records.
                            No new endpoint. No second CRUD. No writes
                            on toggle. Default collapsed; whole section
                            omitted when no genuinely unassigned rows
                            exist. */}
                        <UnassignedAIBreakdownList
                            apiBase={apiBase}
                            projectId={projectId}
                            mode="scene"
                            targetId={sceneId}
                            testidPrefix="scene-wide-ai"
                        />

                        {canEditScene ? (
                            <SceneEditDialog
                                projectId={projectId}
                                scene={scene}
                                open={editOpen}
                                onClose={closeEdit}
                                onSaved={(updated) => setScene(updated)}
                                autoFocusField={editAutoFocus}
                            />
                        ) : null}
                    </>
                )}
            </main>
        </div>
    );
}
